import FunctionsIcon from '@mui/icons-material/Functions';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import type { SvgIconProps } from '@mui/material';
import { Box, Chip, Link, Skeleton, Stack, Typography } from '@mui/material';
import type { ComponentType, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  getAnalyticalDatasetDetailPath,
  getAnalyticalDatasetVersionPath,
  getProjectCohortPath,
  getProjectCohortVersionPath,
  getProjectVariablePath,
} from '@verantos-dev/core-constants';
import { borderRadius, letterSpacing } from '@verantos-dev/shared-ui-components/tokens';

import { useAnalyticalVariableUsage, type AvUsageItem } from '../../api/useAnalyticalVariableUsage';

interface UsedInSectionProps {
  avId: string | null | undefined;
  /** Closes the host dialog (e.g. ``AvDenovoPreviewModal``) on a plain row-link click. */
  handleCloseDialog?: () => void;
}

// TODO: cleanup with av_usage_used_in_section ff
const USED_IN_SECTION_FLAG = 'av_usage_used_in_section';

const USAGE_TYPE_KEYS: Record<AvUsageItem['usageType'], string> = {
  cohort: 'epiChat.descriptionTab.usedIn.type.cohort',
  analytical_dataset: 'epiChat.descriptionTab.usedIn.type.analyticalDataset',
  analytical_variable: 'epiChat.descriptionTab.usedIn.type.analyticalVariable',
};

const USAGE_TYPE_ICON: Record<AvUsageItem['usageType'], ComponentType<SvgIconProps>> = {
  cohort: PeopleAltIcon,
  analytical_dataset: TableChartOutlinedIcon,
  analytical_variable: FunctionsIcon,
};

const TYPE_COL_WIDTH = '30%';

const BODY_MAX_HEIGHT = 571;

// table/header token: Roboto Medium 14, lineHeight 24, letterSpacing 0.17.
const headerCellSx = {
  display: 'flex',
  alignItems: 'center',
  height: 56,
  paddingX: 2,
  fontWeight: 500,
  lineHeight: '24px', // TODO: no VDS token for this value — needs designer input
  letterSpacing: `${letterSpacing.md}px`,
  color: 'text.primary',
} as const;

const typeCellSx = {
  width: TYPE_COL_WIDTH,
  flexShrink: 0,
  paddingX: 2,
  paddingY: 2,
  color: 'text.primary',
} as const;

const nameCellSx = {
  flex: 1,
  minWidth: 0,
  paddingX: 2,
  paddingY: 2,
} as const;

/**
 * Path for a usage row's artifact-detail page. Falls back to the profile-level
 * (unversioned) route when the row has no ``versionId`` — a usage row should
 * always carry one since the backend joins through ``latest_*_version_id``,
 * but the fallback keeps the link functional if that ever changes.
 */
const getUsageItemPath = (item: AvUsageItem, fallbackProjectId: string): string | null => {
  const projectId = item.projectId ?? fallbackProjectId;
  if (!projectId) return null;
  switch (item.usageType) {
    case 'cohort':
      return item.versionId
        ? getProjectCohortVersionPath(projectId, item.id, item.versionId)
        : getProjectCohortPath(projectId, item.id);
    case 'analytical_dataset':
      return item.versionId
        ? getAnalyticalDatasetVersionPath(projectId, item.id, item.versionId)
        : getAnalyticalDatasetDetailPath(projectId, item.id);
    case 'analytical_variable':
      return getProjectVariablePath(projectId, item.id);
  }
};

const TableShell = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'grey.300',
      borderRadius: `${borderRadius.md}px`,
      overflow: 'hidden',
      backgroundColor: 'background.paper',
      // TODO: no VDS token for this value — needs designer input (MUI elevation-1 multi-layer shadow)
      boxShadow:
        '0px 1px 3px 0px rgba(0,0,0,0.12), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 2px 1px -1px rgba(0,0,0,0.2)',
    }}
  >
    {children}
  </Box>
);

export const UsedInSection = ({ avId, handleCloseDialog }: UsedInSectionProps) => {
  const { formatMessage } = useIntl();
  // TODO: cleanup with av_usage_used_in_section ff
  const usedInSectionEnabled = useFeatureFlagSplit(USED_IN_SECTION_FLAG, false) as boolean;
  const { data, isLoading } = useAnalyticalVariableUsage(avId, usedInSectionEnabled);
  const items: AvUsageItem[] = data?.data.usages ?? [];
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const chatId = searchParams.get('chat');

  const getUsageItemHref = (item: AvUsageItem): string | null => {
    const path = getUsageItemPath(item, projectId ?? '');
    if (!path) return null;
    return chatId ? `${path}?chat=${chatId}` : path;
  };

  // Skip modified clicks so open-in-new-tab still works.
  const handleRowLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }
    handleCloseDialog?.();
  };

  if (!usedInSectionEnabled) return null;

  const header = (
    <Stack
      direction="row"
      sx={{ borderBottom: '1px solid', borderColor: 'divider', gap: 2 }}
      data-testid="used-in-header"
    >
      <Box sx={{ ...headerCellSx, width: TYPE_COL_WIDTH, flexShrink: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, letterSpacing: `${letterSpacing.md}px` }}
        >
          {formatMessage({ id: 'epiChat.descriptionTab.usedIn.column.type' })}
        </Typography>
      </Box>
      <Box sx={{ ...headerCellSx, flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, letterSpacing: `${letterSpacing.md}px` }}
        >
          {formatMessage({ id: 'epiChat.descriptionTab.usedIn.column.name' })}
        </Typography>
      </Box>
    </Stack>
  );

  if (!isLoading && items.length === 0) {
    return (
      <Box data-testid="artifact-used-in-empty">
        <Typography variant="body1" sx={{ fontWeight: 600, marginBottom: 2 }}>
          {formatMessage({ id: 'epiChat.descriptionTab.section.usedIn' })}
        </Typography>
        <TableShell>
          <Stack
            alignItems="center"
            justifyContent="center"
            gap={1}
            sx={{ paddingY: 4 }}
            data-testid="used-in-empty-state"
          >
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {formatMessage({ id: 'epiChat.descriptionTab.usedIn.empty.title' })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatMessage({ id: 'epiChat.descriptionTab.usedIn.empty.description' })}
            </Typography>
          </Stack>
        </TableShell>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box data-testid="artifact-used-in-loading">
        <Typography variant="body1" sx={{ fontWeight: 600, marginBottom: 2 }}>
          {formatMessage({ id: 'epiChat.descriptionTab.section.usedIn' })}
        </Typography>
        <TableShell>
          {header}
          <Stack
            direction="row"
            alignItems="center"
            sx={{ gap: 2 }}
            data-testid="used-in-row-skeleton"
          >
            <Box sx={typeCellSx}>
              <Skeleton
                variant="rectangular"
                width="80%"
                height={32}
                sx={{ borderRadius: `${borderRadius.md}px` }}
              />
            </Box>
            <Box sx={nameCellSx}>
              <Skeleton
                variant="rectangular"
                width="70%"
                height={32}
                sx={{ borderRadius: `${borderRadius.md}px` }}
              />
            </Box>
          </Stack>
        </TableShell>
      </Box>
    );
  }

  return (
    <Box data-testid="artifact-used-in">
      <Typography variant="body1" sx={{ fontWeight: 600, marginBottom: 2 }}>
        {formatMessage(
          { id: 'epiChat.descriptionTab.section.usedInWithCount' },
          { count: items.length }
        )}
      </Typography>
      <TableShell>
        {header}
        {/* Body — scrolls within a max height when there are many items. */}
        <Box sx={{ maxHeight: BODY_MAX_HEIGHT, overflowY: 'auto' }}>
          {items.map((item, idx) => {
            const ChipIcon = USAGE_TYPE_ICON[item.usageType];
            const href = getUsageItemHref(item);
            return (
              <Stack
                key={`${item.usageType}-${item.id}`}
                direction="row"
                alignItems="center"
                sx={{
                  borderBottom: idx < items.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  gap: 2,
                }}
                data-testid="used-in-row"
              >
                <Box sx={typeCellSx}>
                  <Chip
                    icon={<ChipIcon />}
                    label={formatMessage({ id: USAGE_TYPE_KEYS[item.usageType] })}
                    size="small"
                    // TODO: no VDS token for 3px padding — needs designer input
                    sx={{ height: 'auto', padding: '3px 0', backgroundColor: 'grey.200' }}
                  />
                </Box>
                <Box sx={nameCellSx}>
                  {href ? (
                    <Link
                      component={RouterLink}
                      to={href}
                      variant="body2"
                      data-testid="used-in-row-link"
                      onClick={handleCloseDialog ? handleRowLinkClick : undefined}
                      sx={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.name}
                    </Link>
                  ) : (
                    <Typography variant="body2">{item.name}</Typography>
                  )}
                </Box>
              </Stack>
            );
          })}
        </Box>
      </TableShell>
    </Box>
  );
};
